import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight } from "lucide-react";
import { NAV, NICE_NAV } from "@/data/content";
import { NiceMark } from "@/components/site/NiceLogo";
import { useAuth } from "@/context/AuthContext";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [niceOpen, setNiceOpen] = useState(false);
  const loc = useLocation();
  const isNice = loc.pathname.startsWith("/nice");
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setNiceOpen(false); }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-[9999] transition-all duration-300 border-b ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-vv-border" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <span className={`w-2.5 h-2.5 rounded-full ${isNice ? "bg-vv-gold gold-glow" : "bg-vv-accent accent-glow"} group-hover:scale-125 transition-transform`} />
          <span className="font-display font-extrabold tracking-tight text-lg">VIGYAVED</span>
          <span className="hidden sm:inline text-[10px] font-mono text-vv-muted tracking-[0.2em] mt-0.5">HEALTHCARE</span>
          {isNice && <span className="hidden sm:flex items-center gap-2 pl-3 ml-1 border-l border-vv-navyborder"><NiceMark /></span>}
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) =>
            n.label === "NICE" ? (
              <div key={n.to} className="relative" onMouseEnter={() => setNiceOpen(true)} onMouseLeave={() => setNiceOpen(false)}>
                <Link to={n.to} data-testid="nav-nice"
                  className="px-3 py-2 text-sm text-vv-fg/80 hover:text-vv-accent transition-colors flex items-center gap-1">
                  {n.label}
                </Link>
                <AnimatePresence>
                  {niceOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 top-full pt-3 w-[300px]">
                      <div className="bg-black/90 backdrop-blur-xl border border-vv-border rounded-xl p-2">
                        <div className="px-3 py-2 text-[10px] font-mono tracking-[0.2em] text-vv-muted">NEXORIA INSTITUTE — NICE</div>
                        {NICE_NAV.map((s) => (
                          <Link key={s.to} to={s.to} data-testid={`nav-nice-${s.to.split("/").pop()}`}
                            className="flex items-center justify-between px-3 py-2 rounded-lg text-sm text-vv-fg/80 hover:text-vv-accent hover:bg-white/5 transition-colors">
                            {s.label}<ChevronRight size={14} className="opacity-40" />
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={`px-3 py-2 text-sm transition-colors ${loc.pathname === n.to ? "text-vv-accent" : "text-vv-fg/80 hover:text-vv-accent"}`}>
                {n.label}
              </Link>
            )
          )}
          {user ? (
            <Link to="/member" data-testid="nav-member"
              className={`px-3 py-2 text-sm transition-colors ${isNice ? "text-vv-gold2 hover:opacity-80" : "text-vv-accent hover:text-white"}`}>Member area</Link>
          ) : (
            <Link to="/login" data-testid="nav-signin"
              className="px-3 py-2 text-sm text-vv-fg/80 hover:text-vv-accent transition-colors">Sign in</Link>
          )}
          <Link to="/contact" data-testid="nav-cta"
            className={`ml-3 px-4 py-2 text-sm font-medium rounded-full transition-colors ${isNice ? "bg-gradient-to-b from-vv-gold2 to-vv-gold text-vv-navy" : "bg-vv-accent text-black hover:bg-white"}`}>
            Get in touch
          </Link>
        </nav>

        <button data-testid="mobile-menu-toggle" onClick={() => setOpen((o) => !o)} className="lg:hidden text-vv-fg">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}
            className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-vv-border">
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} data-testid={`m-nav-${n.label.toLowerCase()}`}
                  className="py-2 text-vv-fg/80 hover:text-vv-accent">{n.label}</Link>
              ))}
              <Link to={user ? "/member" : "/login"} data-testid="m-nav-auth" className="py-2 text-vv-accent">{user ? "Member area" : "Sign in"}</Link>
              <div className="mt-2 pt-2 border-t border-vv-border text-[10px] font-mono tracking-[0.2em] text-vv-muted">NICE</div>
              {NICE_NAV.map((s) => (
                <Link key={s.to} to={s.to} className="py-1.5 text-sm text-vv-fg/60 hover:text-vv-accent">{s.label}</Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
